import { useEffect, useCallback, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { HealthAndSafety as HealthIcon } from '@mui/icons-material';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

const SystemHealth = () => {
  const [healthRows, setHealthRows] = useState([]);
  const [performanceRows, setPerformanceRows] = useState([]);
  const [hardwareRows, setHardwareRows] = useState([]);
  const [alertRows, setAlertRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceError, setPerformanceError] = useState(null);
  const [hardwareError, setHardwareError] = useState(null);
  const [alertError, setAlertError] = useState(null);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setPerformanceError(null);
      setHardwareError(null);
      setAlertError(null);

      if (!supabase) {
        throw new Error('Supabase client not configured.');
      }

      const [
        { data: healthData, error: healthError },
        { data: performanceData, error: perfError },
        { data: hardwareData, error: hardwareError },
        { data: alertsData, error: alertsError }
      ] = await Promise.all([
        supabase
          .from('system_health')
          .select(`
            recorded_at,
            service,
            status,
            latency_ms,
            error_count,
            last_success_at
          `)
          .order('recorded_at', { ascending: false })
          .limit(50),
        supabase
          .from('performance_metrics')
          .select(`
            recorded_at,
            service,
            cpu_percent,
            mem_percent,
            mem_used_mb,
            mem_total_mb,
            temperature_c,
            latency_ms
          `)
          .order('recorded_at', { ascending: false })
          .limit(50),
        supabase
          .from('hardware_status')
          .select(`
            recorded_at,
            device_type,
            device_name,
            status,
            metric_name,
            metric_value,
            metric_unit
          `)
          .order('recorded_at', { ascending: false })
          .limit(50),
        supabase
          .from('system_alerts')
          .select(`
            created_at,
            resolved_at,
            service,
            severity,
            alert_type,
            message,
            status
          `)
          .order('created_at', { ascending: false })
          .limit(50)
      ]);

      if (healthError) throw healthError;
      if (perfError) setPerformanceError(perfError.message);
      if (hardwareError) setHardwareError(hardwareError.message);
      if (alertsError) setAlertError(alertsError.message);

      setHealthRows(healthData || []);
      setPerformanceRows(performanceData || []);
      setHardwareRows(hardwareData || []);
      setAlertRows(alertsData || []);
    } catch (err) {
      console.error('Error loading system health:', err);
      setError(err.message);
      setHealthRows([]);
      setPerformanceRows([]);
      setHardwareRows([]);
      setAlertRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok': return 'success';
      case 'warn': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'success.main' }}>
            <HealthIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold">
            ⚡ System Health
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Monitor system performance, data quality, hardware status, and operational metrics.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🏥 System Status Overview
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading system health...</Typography>
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error loading system health: {error}
              </Alert>
            ) : healthRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No data.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Latency (ms)</TableCell>
                      <TableCell align="right">Errors</TableCell>
                      <TableCell>Last Success</TableCell>
                      <TableCell>Recorded At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {healthRows.map((row, index) => (
                      <TableRow key={`${row.service}-${row.recorded_at}-${index}`}>
                        <TableCell>{row.service}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            color={getStatusColor(row.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {row.latency_ms ?? '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row.error_count ?? 0}
                        </TableCell>
                        <TableCell>
                          {row.last_success_at
                            ? new Date(row.last_success_at).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.recorded_at
                            ? new Date(row.recorded_at).toLocaleString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 Performance Metrics
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading performance metrics...</Typography>
              </Box>
            ) : performanceError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error loading performance metrics: {performanceError}
              </Alert>
            ) : performanceRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No data.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">CPU %</TableCell>
                      <TableCell align="right">Mem %</TableCell>
                      <TableCell align="right">Mem Used</TableCell>
                      <TableCell align="right">Temp (C)</TableCell>
                      <TableCell align="right">Latency (ms)</TableCell>
                      <TableCell>Recorded At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceRows.map((row, index) => (
                      <TableRow key={`${row.service}-${row.recorded_at}-${index}`}>
                        <TableCell>{row.service}</TableCell>
                        <TableCell align="right">{row.cpu_percent ?? '—'}</TableCell>
                        <TableCell align="right">{row.mem_percent ?? '—'}</TableCell>
                        <TableCell align="right">
                          {row.mem_used_mb != null && row.mem_total_mb != null
                            ? `${row.mem_used_mb}/${row.mem_total_mb} MB`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">{row.temperature_c ?? '—'}</TableCell>
                        <TableCell align="right">{row.latency_ms ?? '—'}</TableCell>
                        <TableCell>
                          {row.recorded_at
                            ? new Date(row.recorded_at).toLocaleString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔧 Hardware Status
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading hardware status...</Typography>
              </Box>
            ) : hardwareError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error loading hardware status: {hardwareError}
              </Alert>
            ) : hardwareRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No data.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Device</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Metric</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell>Recorded At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hardwareRows.map((row, index) => (
                      <TableRow key={`${row.device_name}-${row.recorded_at}-${index}`}>
                        <TableCell>{`${row.device_type}: ${row.device_name}`}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            color={getStatusColor(row.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{row.metric_name ?? '—'}</TableCell>
                        <TableCell align="right">
                          {row.metric_value != null
                            ? `${row.metric_value}${row.metric_unit || ''}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.recorded_at
                            ? new Date(row.recorded_at).toLocaleString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📝 System Logs & Alerts
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading alerts...</Typography>
              </Box>
            ) : alertError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error loading alerts: {alertError}
              </Alert>
            ) : alertRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No data.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Severity</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Resolved</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alertRows.map((row, index) => (
                      <TableRow key={`${row.alert_type}-${row.created_at}-${index}`}>
                        <TableCell>
                          <Chip
                            label={row.severity}
                            size="small"
                            color={getStatusColor(row.severity === 'critical' ? 'error' : row.severity)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{row.alert_type}</TableCell>
                        <TableCell>{row.message}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>
                          {row.created_at
                            ? new Date(row.created_at).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.resolved_at
                            ? new Date(row.resolved_at).toLocaleString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SystemHealth;
